import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, DeleteModal, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import {
  type FeePaidBy,
  useAdminOrganizer,
  useOrganizerBilling,
  useReactivateOrganizerRole,
  useRemoveOrganizerRole,
  useRenameOrganization,
  useSaveOrganizerBilling,
} from '@hooks'
import { commissionBpsToInput, formatCount, formatEventPrice, parseCommissionPercent } from '@utils'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { OptionChips } from '../events/components/OptionChips'
import { AdminEventRow } from './components/AdminEventRow'
import { AdminPage } from './components/AdminPage'
import { PersonAvatar } from './components/PersonAvatar'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminOrganizerDetail'>

/** One organizer: who they are, their ticketing commission, and role removal. */
export const AdminOrganizerDetail: React.FC<Props> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const { accountId } = route.params
  const organizer = useAdminOrganizer(accountId)
  // A removed organizer has no commission setting: do not even ask for it.
  const isActive = organizer.data?.isOrganizer === true
  const billing = useOrganizerBilling(accountId, isActive)
  const rename = useRenameOrganization(accountId)
  const reactivate = useReactivateOrganizerRole(accountId)
  const save = useSaveOrganizerBilling(accountId)
  const remove = useRemoveOrganizerRole(accountId)

  const [rate, setRate] = useState('')
  const [feePaidBy, setFeePaidBy] = useState<FeePaidBy>('attendee')
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [organization, setOrganization] = useState('')

  // Fill the form once from the server; later refetches must not overwrite what is being typed.
  const filled = useRef(false)
  useEffect(() => {
    if (billing.data && billing.isFetchedAfterMount && !filled.current) {
      filled.current = true
      setRate(commissionBpsToInput(billing.data.commissionRateBps))
      setFeePaidBy(billing.data.feePaidBy)
    }
  }, [billing.data, billing.isFetchedAfterMount])

  const nameFilled = useRef(false)
  useEffect(() => {
    if (organizer.data && organizer.isFetchedAfterMount && !nameFilled.current) {
      nameFilled.current = true
      setOrganization(organizer.data.organizationName ?? '')
    }
  }, [organizer.data, organizer.isFetchedAfterMount])

  const bps = parseCommissionPercent(rate)
  const rateInvalid = rate.trim() !== '' && bps === null
  const canSave = bps !== null && !save.isPending && !!billing.data
  const person = organizer.data
  const trimmedName = organization.trim()
  const nameInvalid = trimmedName.length < 2
  const nameChanged = trimmedName !== (person?.organizationName ?? '')
  const money = (cents: number) => formatEventPrice(cents, currentLanguage)

  return (
    <AdminPage
      title={t('admin.organizer.title')}
      onBack={() => navigation.navigate('AdminOrganizers')}
      active="organizers"
    >
      {organizer.isLoading || !person ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : (
        <>
          <View style={styles.card}>
            <View style={styles.person}>
              <PersonAvatar
                uri={person.avatarUrl}
                firstName={person.firstName}
                lastName={person.lastName}
                size={56}
              />
              <View style={styles.personBody}>
                <Text type="subtitleBold" numberOfLines={1}>
                  {`${person.firstName} ${person.lastName}`.trim()}
                </Text>
                {person.organizationName ? (
                  <Text type="bodySmallBold" color={theme.colors.primary} numberOfLines={1}>
                    {person.organizationName}
                  </Text>
                ) : null}
                <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
                  {person.email}
                </Text>
                <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                  {t('admin.organizers.eventCount', { count: person.eventCount })}
                </Text>
              </View>
            </View>
          </View>

          {!person.isOrganizer ? (
            <View style={styles.banner} testID="admin-organizer-removed">
              <Text type="subtitleBold" color={theme.colors.error}>
                {t('admin.organizer.removedTitle')}
              </Text>
              <Text type="bodySmall" color={theme.colors.secondary}>
                {t('admin.organizer.removedHint')}
              </Text>
              <Button
                testID="admin-organizer-reactivate"
                title={t('admin.organizer.reactivate')}
                disabled={reactivate.isPending}
                onPress={() => reactivate.mutate()}
              />
            </View>
          ) : null}

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.organizer.organizationTitle')}</Text>
            <TextInput
              testID="admin-organization-input"
              title={t('admin.organizer.organizationLabel')}
              value={organization}
              onChangeText={setOrganization}
              maxLength={120}
              hasError={organization !== '' && nameInvalid}
              errorMessage={
                organization !== '' && nameInvalid ? t('admin.organizer.organizationInvalid') : undefined
              }
            />
            <Button
              testID="admin-organization-save"
              title={t('admin.organizer.saveName')}
              disabled={nameInvalid || !nameChanged || rename.isPending}
              onPress={() => rename.mutate(trimmedName)}
            />
          </View>

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.organizer.statsTitle')}</Text>
            <View style={styles.stats}>
              <Stat label={t('admin.organizer.statEvents')} value={formatCount(person.stats.eventCount, currentLanguage)} testID="admin-organizer-stat-events" />
              <Stat label={t('admin.organizer.statAttendees')} value={formatCount(person.stats.attendeeCount, currentLanguage)} testID="admin-organizer-stat-attendees" />
              <Stat label={t('admin.organizer.statPaid')} value={money(person.stats.paidCents)} testID="admin-organizer-stat-paid" />
              <Stat label={t('admin.organizer.statCommission')} value={money(person.stats.commissionCents)} testID="admin-organizer-stat-commission" />
            </View>
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
              {t('admin.overview.paidOnly')}
            </Text>
          </View>

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.organizer.eventsTitle')}</Text>
            {person.events.length === 0 ? (
              <Text type="bodySmall" color={theme.colors.greyBlue} testID="admin-organizer-no-events">
                {t('admin.organizer.noEvents')}
              </Text>
            ) : (
              person.events.map((event) => (
                <AdminEventRow
                  key={event.id}
                  event={event}
                  onPress={() => navigation.navigate('AdminEventDetail', { eventId: event.id })}
                />
              ))
            )}
          </View>

          {person.isOrganizer ? (
            <>
          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.organizer.commissionTitle')}</Text>
            {billing.isError ? (
              <Text type="bodySmall" color={theme.colors.error} testID="admin-billing-error">
                {t('admin.organizer.billingUnavailable')}
              </Text>
            ) : (
              <>
                <TextInput
                  testID="admin-commission-input"
                  title={t('admin.organizer.commissionLabel')}
                  value={rate}
                  onChangeText={setRate}
                  keyboardType="decimal-pad"
                  hasError={rateInvalid}
                  errorMessage={rateInvalid ? t('admin.organizer.commissionInvalid') : undefined}
                />
                <Text
                  type="bodySmallBold"
                  color={bps === 0 ? theme.colors.green : theme.colors.secondary}
                  testID="admin-commission-summary"
                >
                  {bps === null
                    ? ' '
                    : bps === 0
                      ? t('admin.organizer.noCommission')
                      : t('admin.organizer.commissionSummary', { rate: commissionBpsToInput(bps) })}
                </Text>
                <OptionChips<FeePaidBy>
                  testID="admin-fee-paid-by"
                  label={t('admin.organizer.feePaidBy')}
                  options={[
                    { value: 'attendee', label: t('admin.organizer.feeAttendee') },
                    { value: 'organizer', label: t('admin.organizer.feeOrganizer') },
                  ]}
                  value={feePaidBy}
                  onChange={(value) => value && setFeePaidBy(value)}
                />
                <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                  {t('admin.organizer.appliesToFuture')}
                </Text>
                <Button
                  title={t('admin.organizer.save')}
                  disabled={!canSave}
                  onPress={() => {
                    if (bps !== null) save.mutate({ commissionRateBps: bps, feePaidBy })
                  }}
                />
              </>
            )}
          </View>

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.organizer.roleTitle')}</Text>
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('admin.organizer.roleHint')}
            </Text>
            <Button
              title={t('admin.organizer.removeRole')}
              isRed
              onPress={() => setConfirmRemove(true)}
            />
          </View>
            </>
          ) : null}
        </>
      )}
      <DeleteModal
        isVisible={confirmRemove}
        setIsVisible={setConfirmRemove}
        title={t('admin.organizer.removeRole')}
        description={t('admin.organizer.removeConfirm')}
        deleteLabel={t('admin.organizer.removeRole')}
        onDelete={() =>
          remove.mutate(undefined, { onSuccess: () => navigation.navigate('AdminOrganizers') })
        }
      />
    </AdminPage>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    banner: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.errorSoft,
      borderWidth: 1,
      borderColor: theme.colors.error,
    },
    stats: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.md },
    person: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
    personBody: { flex: 1, minWidth: 0, gap: 2 },
  })

const Stat: React.FC<{ label: string; value: string; testID: string }> = ({ label, value, testID }) => {
  const theme = useTheme()
  return (
    <View style={{ minWidth: 110, flexGrow: 1, flexBasis: 110 }}>
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
        {label}
      </Text>
      <Text type="subtitleBold" testID={testID}>
        {value}
      </Text>
    </View>
  )
}
